import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Grid,
  Stack,
  useToast,
  Heading,
  Box,
  Flex,
  Button,
  Text,
  Input,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  IconButton,
  GridItem,
  Icon,
  Select,
} from "@chakra-ui/react";
import { Loading } from "../../../components/Loading";
import useDeleteToast from "../../../components/DeleteToast";
import { DeleteAlert } from "../../../components/DeleteAlert";
import { Unauthorized } from "../../../components/Unauthorized";
import { FiMoreVertical, FiSearch, FiUsers } from "react-icons/fi";
import { CgAddR } from "react-icons/cg";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Link, useNavigate } from "react-router-dom";
import {
  getDriversList,
  deleteDriver,
} from "../../../actions/inventory/driver.action";
import { FaSackDollar } from "react-icons/fa6";
import { FaFileInvoiceDollar, FaHandHoldingUsd } from "react-icons/fa";
import { TbArrowsSort } from "react-icons/tb";
import { ImFilter } from "react-icons/im";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import {
  DRIVER_OPERATIONAL_STATUS,
  DRIVER_OPERATIONAL_STATUS_COLOR,
  DRIVER_OPERATIONAL_STATUS_LABEL,
  DRIVER_OPERATIONAL_STATUS_OPTIONS,
} from "../../../constants/driverStatus";

const Drivers = () => {
  const toast = useToast();
  const showDeleteToast = useDeleteToast();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [query, setQuery] = useState("");
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDrivers, setTotalDrivers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [globalStats, setGlobalStats] = useState({
    total: 0,
    [DRIVER_OPERATIONAL_STATUS.ONLINE]: 0,
    [DRIVER_OPERATIONAL_STATUS.IDLE]: 0,
    [DRIVER_OPERATIONAL_STATUS.ON_TRIP]: 0,
    [DRIVER_OPERATIONAL_STATUS.OFFLINE]: 0,
    [DRIVER_OPERATIONAL_STATUS.SUSPENDED]: 0,
  });

  const [sortDir, setSortDir] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const debounceTimer = useRef(null);
  const standardOptions = [10, 20, 50, 100];

  const accountStatusMap = {
    active: "Active",
    inactive: "Inactive",
    suspended: "Suspended",
    blacklisted: "Blacklisted",
  };

  const accountStatusColorMap = {
    active: "green",
    inactive: "gray",
    suspended: "orange",
    blacklisted: "red",
  };

  const getDocStatus = (documentation) => {
    if (!documentation) return "-";
    const dl = documentation?.drivingLicensePhotos || {};
    const aadhaar = documentation?.aadhaarCardPhotos || documentation?.adhaarCardPhotos || {};
    const pan = documentation?.panCardPhotos || {};
    const hasAny = [dl.front, dl.back, aadhaar.front, aadhaar.back, pan.front].some(
      (v) => typeof v === "string" && v.trim().length > 0
    );
    return hasAny ? "Available" : "-";
  };

  const loadData = useCallback(async () => {
    setFetching(true);
    try {
      const {
        data: apiData,
        totalDrivers: total,
        totalPages: pages,
        globalStats,
      } = await getDriversList(currentPage, itemsPerPage, query, statusFilter);

      setGlobalStats(globalStats || {
        total: 0,
        [DRIVER_OPERATIONAL_STATUS.UNASSIGNED]: 0,
        [DRIVER_OPERATIONAL_STATUS.ON_TRIP]: 0,
        [DRIVER_OPERATIONAL_STATUS.SUSPENDED]: 0,
        [DRIVER_OPERATIONAL_STATUS.STANDBY]: 0,
      });
      setTotalDrivers(total ?? 0);
      setTotalPages(pages ?? 1);

      const formattedData = apiData.map((item) => {
        const rawStatus = item?.aditionalDetails?.operationalStatus;
        const operationalStatus = Object.values(DRIVER_OPERATIONAL_STATUS).includes(rawStatus)
          ? rawStatus
          : DRIVER_OPERATIONAL_STATUS.UNASSIGNED;
        const accountStatus = item?.aditionalDetails?.accountStatus || "active";
        return {
          id: item._id,
          uniqueDriverId: item?.uniqueDriverId || "-",
          name: item?.name || "-",
          dob: item?.dob || "-",
          mobileNumber: item?.contactDetails?.mobileNumber || "-",
          state: item?.contactDetails?.state || "-",
          docs: getDocStatus(item?.documentation),
          driverType: item?.driverType || "driver_with_vehicle",
          operationalStatus,
          operationalStatusColor: DRIVER_OPERATIONAL_STATUS_COLOR[operationalStatus] || "gray",
          accountStatus,
          accountStatusColor: accountStatusColorMap[accountStatus] || "gray",
        };
      });

      setData(formattedData);
    } catch (error) {
      setError(error.message);
      toast({
        title: "Failed to fetch data!",
        description: error.message,
        status: "error",
        duration: 9000,
      });
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, query, statusFilter]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setQuery(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearSearch = () => {
    setSearchInput("");
    setQuery("");
    setCurrentPage(1);
  };

  // Client-side sort only
  const sortedData = useMemo(() => {
    if (!sortDir) return data;
    return [...data].sort((a, b) => {
      if (!a.name || !b.name) return 0;
      return sortDir === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    });
  }, [data, sortDir]);

  const handleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteDriver(deleteConfirm.id);
      showDeleteToast("Driver deleted successfully");
      loadData();
    } catch (err) {
      toast({
        title: "Failed to delete driver",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const perPageOptions = useMemo(() => {
    return Array.from(new Set([...standardOptions, itemsPerPage]))
      .sort((a, b) => a - b);
  }, [itemsPerPage]);

  const getPagesToShow = () => {
    if (totalPages === 0) return [];
    let startPage = 1;
    let endPage = totalPages;
    if (totalPages > 5) {
      if (currentPage <= 3) { startPage = 1; endPage = 5; }
      else if (currentPage >= totalPages - 2) { startPage = totalPages - 4; endPage = totalPages; }
      else { startPage = currentPage - 2; endPage = currentPage + 2; }
    }
    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  if (loading) return <Loading />;
  if (error === "Unauthorized user") return <Unauthorized />;

  return (
    <>
      <DeleteAlert
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
        onDelete={onDeleteConfirm}
        label="Driver"
        itemName="Driver"
      />
      <Stack w={"100%"} justifyContent={"center"} spacing={10} p={6}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <BreadCrumbMaker
            items={[
              { link: "", title: "Fleet Management" },
              { link: "/fleet-management/driver", title: "Drivers", on: true },
            ]}
          />
          <Button
            borderRadius={5}
            size={"sm"}
            as={Link}
            to={"/fleet-management/driver/add"}
            px={10}
            fontWeight={0}
            py={5}
          >
            <Flex gap={1} alignItems={"center"}>
              <CgAddR /> <Text>Add Driver</Text>
            </Flex>
          </Button>
        </Flex>

        {/* Summary Cards */}
        <Box>
          <Grid
            gridGap={6}
            templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, minmax(250px, 1fr))" }}
          >
            <GridItem key="driversOnDuty">
              <Box bg="white" p={4} rounded="md" display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" h="120px" gap={2} borderRadius="3xl">
                <Box bg={"#FFF5D9"} w={18} h={18} p={30} mx={4} display="flex" alignItems="center" justifyContent="center" borderRadius="full">
                  <Icon as={FaSackDollar} boxSize={6} color={"#FFBB38"} />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">Drivers Online</Text>
                  <Text fontSize="25px">{globalStats?.[DRIVER_OPERATIONAL_STATUS.ONLINE] || 0}</Text>
                </Flex>
              </Box>
            </GridItem>

            <GridItem key="IdleDrivers">
              <Box bg="white" p={4} rounded="md" display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" h="120px" gap={2} borderRadius="3xl">
                <Box bg={"#E7EDFF"} w={18} h={18} p={30} mx={4} display="flex" alignItems="center" justifyContent="center" borderRadius="full">
                  <Icon as={FaHandHoldingUsd} boxSize={6} color={"#396AFF"} />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">Idle Drivers</Text>
                  <Text fontSize="25px">{globalStats?.[DRIVER_OPERATIONAL_STATUS.IDLE] || 0}</Text>
                </Flex>
              </Box>
            </GridItem>

            <GridItem key="MyDrivers">
              <Box bg="white" p={4} rounded="md" display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" h="120px" gap={2} borderRadius="3xl">
                <Box bg={"#FFE0EB"} w={18} h={18} p={30} mx={4} display="flex" alignItems="center" justifyContent="center" borderRadius="full">
                  <Icon as={FaFileInvoiceDollar} boxSize={6} color={"#FF82AC"} />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">My Drivers</Text>
                  <Text fontSize="25px">{globalStats?.total || 0}</Text>
                </Flex>
              </Box>
            </GridItem>
          </Grid>
        </Box>

        {/* Search, Sort, Filter Bar */}
        <Flex justifyContent={"space-between"} flexDirection={{ base: "column", md: "row" }}>
          <Heading fontSize={"xl"} py={1} color={"#333B69"}>Driver List</Heading>
          <Flex w="100%" maxW="600px" gap={3} justifyContent={"flex-end"}>

            {/* Search */}
            <Flex gap={0} alignItems="center" w="100%" maxW="300px" bg="white" px={3} h="40px" borderRadius={"13"}>
              <FiSearch color="#718EBF" fontSize="1rem" />
              <Input
                placeholder="Search here"
                border="none"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                _focus={{ outline: "none", boxShadow: "none" }}
                fontSize="sm"
                h="full"
                _placeholder={{ color: "#718EBF" }}
              />
              {searchInput && (
                <IconButton
                  aria-label="Clear search"
                  icon={<Text fontSize="lg">×</Text>}
                  size="sm"
                  variant="ghost"
                  onClick={clearSearch}
                />
              )}
            </Flex>

            {/* Sort */}
            <Flex
              gap={2}
              alignItems="center"
              justifyContent="center"
              w="100%"
              maxW="91px"
              bg="white"
              px={3}
              h="40px"
              borderRadius={"13"}
              cursor="pointer"
              onClick={handleSort}
            >
              <TbArrowsSort color="#718EBF" fontSize="1rem" />
              <Text fontSize="sm" color="#718EBF">
                Sort {sortDir ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </Text>
            </Flex>

            {/* Filter */}
            <Flex
              gap={2}
              alignItems="center"
              justifyContent="center"
              w="100%"
              maxW="160px"
              bg="white"
              px={3}
              h="40px"
              borderRadius={"13"}
            >
              <ImFilter color="#718EBF" fontSize="1rem" />
              <Select
                fontSize="sm"
                width="auto"
                value={statusFilter}
                onChange={handleFilterChange}
                placeholder="All Status"
                h="28px"
                border="none"
                _focus={{ outline: "none", boxShadow: "none" }}
              >
                {DRIVER_OPERATIONAL_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {DRIVER_OPERATIONAL_STATUS_LABEL[status]}
                  </option>
                ))}
              </Select>
            </Flex>
          </Flex>
        </Flex>

        {/* Table */}
        <Box p={7} bg={"white"} overflowX={"auto"} borderRadius={"3xl"} minH="420px">
          <Table size="sm" bg={"white"} fontSize={"small"}>
            <Thead>
              <Tr>
                <Th color={"#718EBF"}>S.No</Th>
                <Th color={"#718EBF"}>Driver ID</Th>
                <Th color={"#718EBF"}>Driver Name</Th>
                <Th color={"#718EBF"}>DOB</Th>
                <Th color={"#718EBF"}>Driver Type</Th>
                <Th color={"#718EBF"}>Mobile Number</Th>
                <Th color={"#718EBF"}>State</Th>
                <Th color={"#718EBF"}>Document</Th>
                <Th color={"#718EBF"}>Operational Status</Th>
                <Th color={"#718EBF"}>Account Status</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedData?.length === 0 ? (
                <Tr>
                  <Td colSpan={10} textAlign="center" py={16}>
                    <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={3}>
                      <Box bg="#F1F4FF" p={5} borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                        <FiUsers size={32} color="#718EBF" />
                      </Box>
                      <Text color="#333B69" fontSize="md" fontWeight="semibold">No Records Found</Text>
                      <Text color="#718EBF" fontSize="sm">
                        {statusFilter || searchInput
                          ? "Try adjusting your search or filter criteria"
                          : "No drivers have been added yet"}
                      </Text>
                      {(statusFilter || searchInput) && (
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          mt={1}
                          onClick={() => { clearSearch(); setStatusFilter(""); }}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </Flex>
                  </Td>
                </Tr>
              ) : (
                sortedData.map((item, index) => (
                  <Tr key={item.id} onClick={() => navigate(`/fleet-management/driver/${item?.id}`)}>
                    <Td>{(currentPage - 1) * itemsPerPage + index + 1}</Td>
                    <Td>{item.uniqueDriverId}</Td>
                    <Td>{item.name}</Td>
                    <Td>{item.dob}</Td>
                    <Td>{item.driverType === "vendor_driver" ? "Vendor Driver" : "Driver with Vehicle"}</Td>
                    <Td>{item.mobileNumber}</Td>
                    <Td>{item.state}</Td>
                    <Td>{item.docs}</Td>
                    <Td>
                      <Badge colorScheme={item.operationalStatusColor} px={3} py={1} rounded="lg">
                        {DRIVER_OPERATIONAL_STATUS_LABEL[item.operationalStatus]}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={item.accountStatusColor} px={3} py={1} rounded="lg">
                        {accountStatusMap[item.accountStatus]}
                      </Badge>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton as={IconButton} icon={<FiMoreVertical />} variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} />
                        <MenuList borderRadius={0}>
                          <MenuItem as={Link} to={`/fleet-management/driver/edit/${item.id}`} onClick={(e) => e.stopPropagation()} borderRadius={0}>
                            Edit
                          </MenuItem>
                          <MenuItem onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} borderRadius={0} color="red.500">
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>

          {/* Pagination */}
          <Flex justifyContent="space-between" alignItems="center" mt={4} gap={2}>
            <Flex align="center" gap={4} wrap="wrap" justify="center" mt={4}>
              <Text fontSize="sm" color={"#8B909A"}>Showing:</Text>
              <Select
                size="sm"
                width="fit-content"
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {perPageOptions.map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </Select>
              <Text fontSize="sm" color={"#8B909A"}>of {totalDrivers}</Text>
            </Flex>

            <Flex alignItems="center" mt={4} gap={2}>
              <Button
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
              >
                <GoChevronLeft />
              </Button>

              {totalDrivers === 0 ? (
                <Button size="sm" bgColor="#F1F2F6" isDisabled>
                  <Text color="#8B909A">1</Text>
                </Button>
              ) : (
                getPagesToShow().map((i) => (
                  <Button
                    key={i}
                    size="sm"
                    bgColor={i === currentPage ? "#0F60FF" : "#F1F2F6"}
                    onClick={() => setCurrentPage(i)}
                  >
                    <Text color={i === currentPage ? "#FFFFFF" : "#8B909A"}>{i}</Text>
                  </Button>
                ))
              )}

              <Button
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                isDisabled={currentPage === totalPages || totalDrivers === 0}
              >
                <GoChevronRight />
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Stack>
    </>
  );
};

export default Drivers;
